import { ArrowUpRightIcon } from "@wags/ui/product-icons.tsx";
import { cn } from "@wags/ui/ui-utils";
import { Fragment } from "react";
import {
    type BarePolicyUrl,
    collapseBlankLines,
    linkifyPolicyText,
    type PolicyView,
    type PreviewClamp,
    splitParagraphs,
} from "./provider-policies";

const LINK_CLASS =
    "break-words underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow";

function LinkifiedText({ text }: { text: string }) {
    return linkifyPolicyText(text).map((segment, index) =>
        segment.kind === "link" ? (
            <a
                key={index}
                href={segment.href}
                target="_blank"
                rel="noopener noreferrer"
                className={LINK_CLASS}
            >
                {segment.label}
            </a>
        ) : (
            <Fragment key={index}>{segment.text}</Fragment>
        ),
    );
}

function BarePolicyLink({ url }: { url: BarePolicyUrl }) {
    return (
        <a
            href={url.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("inline-flex items-center gap-1", LINK_CLASS)}
        >
            Details on {url.host}
            <ArrowUpRightIcon aria-hidden className="size-4 shrink-0" />
        </a>
    );
}

export function PolicyPreview({
    policy,
    clampClassName,
}: {
    policy: PolicyView;
    clampClassName: PreviewClamp["className"];
}) {
    if (policy.bareUrl) {
        return (
            <p className="mt-3 text-body text-doberman-black/60">
                <BarePolicyLink url={policy.bareUrl} />
            </p>
        );
    }
    return (
        <p
            className={cn(
                "mt-3 whitespace-pre-line break-words text-body text-doberman-black/60",
                clampClassName,
            )}
        >
            <LinkifiedText text={collapseBlankLines(policy.text)} />
        </p>
    );
}

export function PolicyFull({ policy }: { policy: PolicyView }) {
    if (policy.bareUrl) {
        return (
            <p className="text-body leading-normal text-doberman-black/80">
                <BarePolicyLink url={policy.bareUrl} />
            </p>
        );
    }
    return (
        <div className="max-w-prose space-y-2 text-body leading-normal text-doberman-black/80">
            {splitParagraphs(policy.text).map((paragraph, index) => (
                <p key={index} className="whitespace-pre-line break-words">
                    <LinkifiedText text={paragraph} />
                </p>
            ))}
        </div>
    );
}
